/**
 * iter281+iter282 — Tutanak Modalı (FAZ 1-4).
 * Personel Takip sağ üstündeki [Tutanak] butonundan açılır.
 * Sekmeler: Yeni Tutanak · Tutanak Geçmişi · Detay/İş Akışı.
 * İş Akışı: AI Öneri (Faz 3) + Savunma Talep/Yükle + AI Değerlendirme + İşveren Kararı + Kapat (Faz 4).
 * AI SONUÇLARI OTOMATİK UYGULANMAZ — kullanıcı manuel "Uygula" ile devreye alır.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Modal, Btn, Input, Select, Textarea } from "@/components/UI";
import {
  FileText, Plus, Eye, Download, Printer, Trash2, Users, Sparkles, Scale,
  ArrowLeft, ShieldQuestion, Upload, Gavel, CheckCircle2, Loader2, AlertTriangle,
  Paperclip, FileArchive, Pencil,
} from "lucide-react";

const STATUS_MAP = {
  draft: { l: "Taslak", c: "bg-slate-100 text-slate-700 border-slate-300" },
  prepared: { l: "Hazırlandı", c: "bg-blue-100 text-blue-700 border-blue-300" },
  signed_uploaded: { l: "İmzalı Yüklendi", c: "bg-emerald-100 text-emerald-700 border-emerald-300" },
  defense_requested: { l: "Savunma İstendi", c: "bg-amber-100 text-amber-800 border-amber-300" },
  defense_received: { l: "Savunma Alındı", c: "bg-purple-100 text-purple-700 border-purple-300" },
  evaluation_completed: { l: "Değerlendirildi", c: "bg-indigo-100 text-indigo-700 border-indigo-300" },
  closed: { l: "Kapatıldı", c: "bg-gray-200 text-gray-700 border-gray-300" },
};
const DECISION_MAP = {
  no_action: "İşlem yapılmadı",
  verbal_warning: "Sözlü Uyarı",
  written_warning: "Yazılı İhtar",
  suspension: "İş Akdi Askıya Alma",
  termination: "İş Akdi Feshi",
};

function StatusBadge({ s }) {
  const m = STATUS_MAP[s] || STATUS_MAP.draft;
  return <span className={`text-[10px] font-semibold px-2 py-0.5 rounded border ${m.c}`}>{m.l}</span>;
}

// iter289 — Genel Ek Belge Yükleme paneli.
// Savunma yükleme (defense_response + AI) akışından bağımsız.
// Kapsam: imzalı tutanak çıktısı, tanık ifadesi, fotoğraf, tıbbi rapor vb.
const ATTACH_CATEGORIES = [
  { v: "signed_copy",       l: "İmzalı Tutanak Çıktısı" },
  { v: "employee_defense",  l: "Personel Savunması" },  // iter292 — Faz 4 AI ile okunabilir
  { v: "witness_statement", l: "Tanık İfadesi" },
  { v: "photo",             l: "Fotoğraf / Görsel" },
  { v: "medical_report",    l: "Tıbbi Rapor" },
  { v: "camera_footage",    l: "Kamera Görüntüsü" },
  { v: "other",             l: "Diğer" },
];

function AttachmentsPanel({ doc, onRefresh }) {
  const [file, setFile] = useState(null);
  const [category, setCategory] = useState("signed_copy");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // iter290 — İç iframe önizleme state
  const [preview, setPreview] = useState(null); // {name, mime, url}

  const upload = async () => {
    if (!file) { setErr("Dosya seçin"); return; }
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", category);
      if (note.trim()) fd.append("note", note.trim());
      await api.postForm(`/personnel/incident-reports/${doc.id}/attachments`, fd);
      setFile(null); setNote(""); setCategory("signed_copy");
      await onRefresh();
    } catch (e) { setErr(e.response?.data?.detail || "Yükleme başarısız"); }
    setBusy(false);
  };
  // iter290 — Yeni sekme yerine sistem içi iframe önizleme.
  const openAtt = async (a) => {
    try {
      const blob = await api.getBlob(`/personnel/incident-reports/${doc.id}/attachments/${a.id}`);
      const url = URL.createObjectURL(blob);
      setPreview({ name: a.original_name, mime: a.mime_type || blob.type, url, category: a.category });
    } catch (e) { alert(e.response?.data?.detail || "Belge açılamadı"); }
  };
  const closePreview = () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };
  const downloadFromPreview = () => {
    if (!preview) return;
    const a = document.createElement("a");
    a.href = preview.url; a.download = preview.name;
    document.body.appendChild(a); a.click(); a.remove();
  };
  // iter290 — Toplu ZIP indir
  const downloadZip = async () => {
    try {
      const blob = await api.getBlob(`/personnel/incident-reports/${doc.id}/attachments-zip`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `Tutanak_${doc.incident_no}_Ekler.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { alert(e.response?.data?.detail || "ZIP indirilemedi"); }
  };
  const removeAtt = async (a) => {
    if (!window.confirm(`"${a.original_name}" belgesi silinsin mi? (Sistem Yöneticisi yetkisi gerekli)`)) return;
    try {
      await api.delete(`/personnel/incident-reports/${doc.id}/attachments/${a.id}`);
      await onRefresh();
    } catch (e) { alert(e.response?.data?.detail || "Silinemedi"); }
  };

  const atts = doc.attachments || [];
  const locked = doc.status === "closed";
  return (
    <div className="border rounded p-3 bg-slate-50/60 border-slate-300" data-testid="ir-attach-panel">
      <div className="font-semibold text-sm text-slate-800 mb-2 flex items-center gap-1.5">
        <FileArchive size={14}/> Ek Belgeler
        <span className="text-[10px] font-normal text-slate-500">
          (imzalı çıktı, tanık ifadesi, fotoğraf, tıbbi rapor vb. — savunma dışı)
        </span>
        <span className="ml-auto text-[10px] text-slate-500">{atts.length} belge</span>
        {atts.length > 0 && (
          <Btn size="sm" variant="secondary" onClick={downloadZip} data-testid="ir-attach-zip">
            <Download size={11}/> Tümünü ZIP indir
          </Btn>
        )}
      </div>

      {/* Yükleme formu */}
      {!locked && (
        <div className="bg-white border rounded p-2 mb-2 space-y-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase text-slate-500 font-semibold">Belge Türü</label>
              <Select value={category} onChange={e => setCategory(e.target.value)} data-testid="ir-attach-cat">
                {ATTACH_CATEGORIES.map(c => <option key={c.v} value={c.v}>{c.l}</option>)}
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase text-slate-500 font-semibold">Dosya (max 20MB)</label>
              <input type="file" onChange={e => setFile(e.target.files?.[0] || null)}
                     data-testid="ir-attach-file"
                     className="w-full text-xs file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"/>
            </div>
          </div>
          <Input placeholder="Not (opsiyonel) — belge hakkında kısa açıklama"
                 value={note} onChange={e => setNote(e.target.value)} data-testid="ir-attach-note"/>
          {err && <div className="text-[11px] text-rose-700">{err}</div>}
          <div className="flex justify-end">
            <Btn size="sm" onClick={upload} disabled={busy || !file} data-testid="ir-attach-upload">
              {busy ? <Loader2 size={12} className="animate-spin"/> : <Upload size={12}/>} Yükle
            </Btn>
          </div>
        </div>
      )}
      {locked && (
        <div className="text-[11px] text-slate-500 italic mb-2">
          Tutanak kapatılmış — yeni belge yüklenemez; mevcut belgeler görüntülenebilir.
        </div>
      )}

      {/* Yüklü belge listesi */}
      {atts.length === 0 ? (
        <div className="text-xs text-slate-400 italic py-2 text-center">Henüz ek belge yok</div>
      ) : (
        <div className="space-y-1">
          {atts.map(a => {
            const cat = ATTACH_CATEGORIES.find(c => c.v === a.category);
            return (
              <div key={a.id} className="flex items-center gap-2 p-1.5 bg-white border rounded text-xs"
                   data-testid={`ir-attach-row-${a.id}`}>
                <Paperclip size={12} className="text-slate-400 shrink-0"/>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <button onClick={() => openAtt(a)} className="text-indigo-700 hover:underline truncate font-medium"
                            data-testid={`ir-attach-open-${a.id}`} title={a.original_name}>
                      {a.original_name}
                    </button>
                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border shrink-0">
                      {cat?.l || a.category || "Diğer"}
                    </span>
                  </div>
                  <div className="text-[10px] text-slate-500">
                    {(a.size / 1024).toFixed(1)} KB · {a.uploaded_by} · {(a.uploaded_at || "").slice(0, 10)}
                    {a.note && <span className="italic"> · {a.note}</span>}
                  </div>
                </div>
                <button onClick={() => openAtt(a)} className="p-1 hover:bg-slate-100 rounded"
                        data-testid={`ir-attach-preview-${a.id}`} title="Önizle">
                  <Eye size={11}/>
                </button>
                <button onClick={() => removeAtt(a)} className="p-1 hover:bg-rose-100 text-rose-600 rounded"
                        data-testid={`ir-attach-del-${a.id}`} title="Sil (Sistem Yöneticisi)">
                  <Trash2 size={11}/>
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* iter290 — Sistem içi belge önizleme modal'ı */}
      {preview && (
        <AttachmentPreviewModal preview={preview} onClose={closePreview}
                                 onDownload={downloadFromPreview}/>
      )}
    </div>
  );
}

// iter290 — Ek belge iframe önizleme (PDF/görsel) — yeni sekme açmadan
// modal içinde önizleme + İndir + Kapat butonları.
function AttachmentPreviewModal({ preview, onClose, onDownload }) {
  const isPdf = (preview.mime || "").includes("pdf")
             || preview.name?.toLowerCase().endsWith(".pdf");
  const isImg = (preview.mime || "").startsWith("image/");
  return (
    <Modal open={true} onClose={onClose} title={preview.name} size="lg">
      <div className="flex flex-col gap-2" data-testid="ir-attach-preview-modal">
        {isPdf && (
          <iframe src={preview.url} title={preview.name}
                  className="w-full border rounded" style={{ height: "70vh" }}/>
        )}
        {isImg && (
          <div className="w-full bg-slate-900 rounded flex items-center justify-center"
               style={{ maxHeight: "70vh" }}>
            <img src={preview.url} alt={preview.name}
                 className="max-w-full max-h-[70vh] object-contain"/>
          </div>
        )}
        {!isPdf && !isImg && (
          <div className="p-6 text-center text-sm text-slate-600 bg-slate-50 border rounded">
            <FileArchive size={32} className="mx-auto mb-2 text-slate-400"/>
            <div>Bu dosya türü ({preview.mime || "bilinmiyor"}) tarayıcıda önizlenemez.</div>
            <div className="text-xs text-slate-500 mt-1">İndirip yerel uygulamada açın.</div>
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Btn size="sm" variant="secondary" onClick={onDownload} data-testid="ir-attach-preview-download">
            <Download size={12}/> İndir
          </Btn>
          <Btn size="sm" variant="secondary" onClick={onClose} data-testid="ir-attach-preview-close">
            Kapat
          </Btn>
        </div>
      </div>
    </Modal>
  );
}

export default function IncidentReportsModal({ open, onClose, focusEmployeeId = null, focusIncidentId = null }) {
  const [tab, setTab] = useState("new");
  const [employees, setEmployees] = useState([]);
  const [reports, setReports] = useState([]);
  const [form, setForm] = useState(defaultForm());
  const [busy, setBusy] = useState(false);
  const [current, setCurrent] = useState(null); // full doc for detail view
  // iter291 — Düzenle & Sil butonları için state (admin-only)
  const [editReport, setEditReport] = useState(null); // report being edited
  const user = (() => { try { return JSON.parse(localStorage.getItem("user") || "{}"); } catch { return {}; } })();
  const isAdmin = user?.role === "admin";

  function defaultForm() {
    return {
      employee_ids: focusEmployeeId ? [focusEmployeeId] : [],
      incident_date: new Date().toISOString().slice(0, 10),
      incident_time: "",
      incident_place: "",
      incident_description: "",
      witness_1_name: "", witness_1_employee_id: "",
      witness_2_name: "", witness_2_employee_id: "",
    };
  }

  const load = async () => {
    const [emps, rep] = await Promise.all([
      api.get("/employees").catch(() => []),
      api.get("/personnel/incident-reports" + (focusEmployeeId ? `?employee_id=${focusEmployeeId}` : "")).catch(() => []),
    ]);
    const empArr = Array.isArray(emps) ? emps : (emps?.employees || []);
    setEmployees(empArr.filter(e => !e.is_deleted));
    setReports(Array.isArray(rep) ? rep : []);
  };
  useEffect(() => {
    if (!open) return;
    setForm(defaultForm());
    setCurrent(null);
    // iter286 — focusIncidentId verilirse otomatik detay/iş akışı sekmesine aç.
    if (focusIncidentId) {
      (async () => {
        await load();
        try {
          const full = await api.get(`/personnel/incident-reports/${focusIncidentId}`);
          setCurrent(full);
          setTab("detail");
        } catch (e) {
          setTab("history");
        }
      })();
    } else {
      setTab("new");
      load();
    }
  }, [open, focusEmployeeId, focusIncidentId]); // eslint-disable-line

  const toggleEmp = (id) => setForm(f => ({
    ...f, employee_ids: f.employee_ids.includes(id)
      ? f.employee_ids.filter(x => x !== id)
      : [...f.employee_ids, id],
  }));

  const save = async () => {
    if (form.employee_ids.length === 0) return alert("En az bir personel seçin");
    if (!form.incident_description.trim()) return alert("Olay açıklaması zorunlu");
    setBusy(true);
    try {
      await api.post("/personnel/incident-reports", form);
      await load();
      setTab("history");
      setForm(defaultForm());
    } catch (e) { alert(e.response?.data?.detail || "Kayıt hatası"); }
    setBusy(false);
  };

  const openPdf = async (r, action = "preview") => {
    try {
      const blob = await api.getBlob(`/personnel/incident-reports/${r.id}/pdf`);
      const url = URL.createObjectURL(blob);
      if (action === "download") {
        const a = document.createElement("a");
        a.href = url; a.download = `${r.incident_no}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
      } else if (action === "print") {
        const w = window.open(url, "_blank");
        if (w) w.onload = () => w.print();
      } else {
        window.open(url, "_blank");
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { alert(e.response?.data?.detail || "PDF alınamadı"); }
  };

  const openDetail = async (r) => {
    try {
      const full = await api.get(`/personnel/incident-reports/${r.id}`);
      setCurrent(full);
      setTab("detail");
    } catch (e) { alert(e.response?.data?.detail || "Detay alınamadı"); }
  };

  const refreshCurrent = async () => {
    if (!current?.id) return;
    try {
      const full = await api.get(`/personnel/incident-reports/${current.id}`);
      setCurrent(full);
      // liste de güncellensin
      const rep = await api.get("/personnel/incident-reports" + (focusEmployeeId ? `?employee_id=${focusEmployeeId}` : ""));
      setReports(Array.isArray(rep) ? rep : []);
    } catch (e) { /* ignore */ }
  };

  return (
    <Modal open={open} onClose={onClose} title="Personel Olay Tutanakları" size="full">
      <div className="flex gap-2 mb-3 border-b flex-wrap">
        <button onClick={() => { setTab("new"); setCurrent(null); }} data-testid="ir-tab-new"
          className={`px-3 py-2 text-sm border-b-2 ${tab === "new" ? "border-indigo-600 text-indigo-700 font-semibold" : "border-transparent text-slate-500"}`}>
          <Plus size={14} className="inline"/> Yeni Tutanak
        </button>
        <button onClick={() => { setTab("history"); setCurrent(null); }} data-testid="ir-tab-history"
          className={`px-3 py-2 text-sm border-b-2 ${tab === "history" ? "border-indigo-600 text-indigo-700 font-semibold" : "border-transparent text-slate-500"}`}>
          <FileText size={14} className="inline"/> Tutanak Geçmişi ({reports.length})
        </button>
        {tab === "detail" && current && (
          <button onClick={() => { setTab("history"); setCurrent(null); }} data-testid="ir-tab-detail"
            className="px-3 py-2 text-sm border-b-2 border-indigo-600 text-indigo-700 font-semibold">
            <Gavel size={14} className="inline"/> İş Akışı: {current.incident_no}
          </button>
        )}
      </div>

      {tab === "new" && (
        <NewIncidentForm form={form} setForm={setForm} employees={employees}
                         toggleEmp={toggleEmp} save={save} busy={busy} onClose={onClose}/>
      )}

      {tab === "history" && (
        <HistoryTable reports={reports} openPdf={openPdf} openDetail={openDetail}
                       isAdmin={isAdmin}
                       onEdit={(r) => { setEditReport(r); }}
                       onDelete={async (r) => {
                         if (!window.confirm(`${r.incident_no} tutanağını KALICI olarak silmek istediğinize emin misiniz?\n(İşlem geri alınamaz. Yüklü savunma dosyası storage'da kalır — delil zinciri korunur.)`)) return;
                         try {
                           await api.delete(`/personnel/incident-reports/${r.id}`);
                           await load();
                         } catch (e) { alert(e.response?.data?.detail || "Silinemedi"); }
                       }}/>
      )}

      {tab === "detail" && current && (
        <DetailWorkflow doc={current} onBack={() => { setTab("history"); setCurrent(null); }}
                        onPdf={() => openPdf(current)} onRefresh={refreshCurrent}
                        isAdmin={isAdmin}
                        onEdit={() => setEditReport(current)}/>
      )}

      {/* iter291 — Tutanak Düzenle Modal (admin-only) */}
      {editReport && (
        <EditIncidentModal report={editReport} employees={employees}
          onClose={() => setEditReport(null)}
          onSaved={async () => {
            setEditReport(null);
            await load();
            if (current && current.id === editReport.id) {
              await refreshCurrent();
            }
          }}/>
      )}
    </Modal>
  );
}

// ===================================================================
// Yeni Tutanak Formu
// ===================================================================
function NewIncidentForm({ form, setForm, employees, toggleEmp, save, busy, onClose }) {
  return (
    <div className="space-y-3" data-testid="ir-form">
      <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-900">
        <b>ℹ</b> Tutanak No otomatik atanır (TUT-YYYY-NNNN). Onaylandıktan sonra sessizce değiştirilemez;
        düzeltme yeni versiyon oluşturur. AI öneri ve savunma akışı <b>Tutanak Geçmişi → İş Akışı</b>'ndan yönetilir.
      </div>
      <div>
        <label className="text-xs uppercase text-slate-500 flex items-center gap-1"><Users size={12}/> İlgili Personel *</label>
        <div className="max-h-32 overflow-y-auto border rounded p-2 space-y-1" data-testid="ir-emp-list">
          {employees.map(e => (
            <label key={e.id} className="flex items-center gap-2 text-xs cursor-pointer">
              <input type="checkbox" checked={form.employee_ids.includes(e.id)}
                     onChange={() => toggleEmp(e.id)} data-testid={`ir-emp-${e.id}`}/>
              <span>{e.first_name} {e.last_name} <span className="text-slate-400">· {e.role || "—"}</span></span>
            </label>
          ))}
          {employees.length === 0 && <div className="text-xs text-slate-400 italic">Personel yok</div>}
        </div>
        <div className="text-[10px] text-slate-500 mt-1">{form.employee_ids.length} seçili</div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <Input label="Olay Tarihi *" type="date" value={form.incident_date}
               onChange={e => setForm({ ...form, incident_date: e.target.value })} data-testid="ir-date"/>
        <Input label="Olay Saati" type="time" value={form.incident_time}
               onChange={e => setForm({ ...form, incident_time: e.target.value })} data-testid="ir-time"/>
        <Input label="Olay Yeri" value={form.incident_place}
               onChange={e => setForm({ ...form, incident_place: e.target.value })} data-testid="ir-place"/>
      </div>
      <Textarea label="Olay Açıklaması *" rows={5} value={form.incident_description}
                onChange={e => setForm({ ...form, incident_description: e.target.value })}
                placeholder="Yaşanan olayı kısa ve olgulara dayalı biçimde yazın."
                data-testid="ir-desc"/>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <WitnessPicker label="Şahit 1" empKey="witness_1_employee_id" nameKey="witness_1_name"
                       form={form} setForm={setForm} employees={employees} tid="ir-w1"/>
        <WitnessPicker label="Şahit 2" empKey="witness_2_employee_id" nameKey="witness_2_name"
                       form={form} setForm={setForm} employees={employees} tid="ir-w2"/>
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <Btn variant="secondary" onClick={onClose} data-testid="ir-cancel">Vazgeç</Btn>
        <Btn onClick={save} disabled={busy} data-testid="ir-save"><FileText size={14}/> Tutanağı Kaydet</Btn>
      </div>
    </div>
  );
}

function WitnessPicker({ label, empKey, nameKey, form, setForm, employees, tid }) {
  return (
    <div>
      <label className="text-xs uppercase text-slate-500">{label}</label>
      <Select value={form[empKey]}
              onChange={e => {
                const emp = employees.find(x => x.id === e.target.value);
                setForm({ ...form, [empKey]: e.target.value,
                          [nameKey]: emp ? `${emp.first_name} ${emp.last_name}` : form[nameKey] });
              }}
              data-testid={`${tid}-sel`}>
        <option value="">— Personelden seç (opsiyonel) —</option>
        {employees.map(e => <option key={e.id} value={e.id}>{e.first_name} {e.last_name}</option>)}
      </Select>
      <Input placeholder="veya manuel Ad Soyad" value={form[nameKey]}
             onChange={e => setForm({ ...form, [nameKey]: e.target.value, [empKey]: "" })}
             data-testid={`${tid}-name`}/>
    </div>
  );
}

// ===================================================================
// Tutanak Geçmişi Tablosu
// ===================================================================
function HistoryTable({ reports, openPdf, openDetail, isAdmin, onEdit, onDelete }) {
  return (
    <div className="max-h-[70vh] overflow-y-auto" data-testid="ir-list">
      {reports.length === 0 && <div className="py-8 text-center text-sm text-slate-400 italic">Kayıt yok</div>}
      <table className="w-full text-xs">
        <thead className="bg-slate-50 border-b sticky top-0">
          <tr>{["Tutanak No","Olay Tarihi","Personel","Yer","Durum","İşlem"].map((h,i) =>
            <th key={i} className="text-left px-2 py-2 font-semibold text-slate-600 uppercase text-[10px]">{h}</th>
          )}</tr>
        </thead>
        <tbody>
          {reports.map(r => (
            <tr key={r.id} className="border-b hover:bg-slate-50" data-testid={`ir-row-${r.id}`}>
              <td className="px-2 py-1.5 font-mono text-indigo-700">
                <button className="hover:underline" onClick={() => openDetail(r)}
                        data-testid={`ir-open-${r.id}`}>{r.incident_no}</button>
              </td>
              <td className="px-2 py-1.5 font-mono text-[10px]">{r.incident_date}</td>
              <td className="px-2 py-1.5">{(r.employees || []).map(e => e.name).join(", ")}</td>
              <td className="px-2 py-1.5">{r.incident_place || "—"}</td>
              <td className="px-2 py-1.5"><StatusBadge s={r.status}/></td>
              <td className="px-2 py-1.5">
                <div className="flex gap-1 flex-wrap">
                  <Btn size="sm" variant="secondary" onClick={() => openDetail(r)} data-testid={`ir-workflow-${r.id}`}>
                    <Gavel size={11}/> İş Akışı
                  </Btn>
                  <Btn size="sm" variant="secondary" onClick={() => openPdf(r, "preview")} data-testid={`ir-preview-${r.id}`}><Eye size={11}/></Btn>
                  <Btn size="sm" variant="secondary" onClick={() => openPdf(r, "download")} data-testid={`ir-download-${r.id}`}><Download size={11}/></Btn>
                  <Btn size="sm" variant="secondary" onClick={() => openPdf(r, "print")} data-testid={`ir-print-${r.id}`}><Printer size={11}/></Btn>
                  {/* iter293 — Düzenle her zaman gösteriliyor; backend zaten admin gate'i uygular
                       ve non-admin 403 mesajı görür. Kullanıcı feedback: "istediğimizde silelim/düzenleyelim" */}
                  <Btn size="sm" variant="secondary" onClick={() => onEdit(r)} data-testid={`ir-edit-${r.id}`}
                       className="!bg-amber-100 hover:!bg-amber-200 !text-amber-800">
                    <Pencil size={11}/> Düzenle
                  </Btn>
                  <Btn size="sm" variant="secondary" onClick={() => onDelete(r)} data-testid={`ir-delete-${r.id}`}
                       className="!bg-rose-100 hover:!bg-rose-200 !text-rose-700">
                    <Trash2 size={11}/> Sil
                  </Btn>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// iter291 — Tutanak Düzenle Modal (admin-only). Sistem Yöneticisi
// imza öncesinde unutulan detayları güncelleyebilir. PATCH endpoint'i
// zaten mevcut versiyonu arşivleyip versiyon +1 yapar.
function EditIncidentModal({ report, employees, onClose, onSaved }) {
  const [form, setForm] = useState({
    employee_ids: report.employee_ids || [],
    incident_date: report.incident_date || "",
    incident_time: report.incident_time || "",
    incident_place: report.incident_place || "",
    incident_description: report.incident_description || "",
    witness_1_name: report.witness_1_name || "",
    witness_1_employee_id: report.witness_1_employee_id || "",
    witness_2_name: report.witness_2_name || "",
    witness_2_employee_id: report.witness_2_employee_id || "",
  });
  const [busy, setBusy] = useState(false);
  const toggleEmp = (id) => setForm(f => ({
    ...f, employee_ids: f.employee_ids.includes(id)
      ? f.employee_ids.filter(x => x !== id) : [...f.employee_ids, id],
  }));
  const save = async () => {
    if (form.employee_ids.length === 0) return alert("En az bir personel seçin");
    if (!form.incident_description.trim()) return alert("Olay açıklaması zorunlu");
    setBusy(true);
    try {
      await api.patch(`/personnel/incident-reports/${report.id}`, form);
      await onSaved();
    } catch (e) { alert(e.response?.data?.detail || "Kayıt hatası"); }
    setBusy(false);
  };
  return (
    <Modal open={true} onClose={onClose} title={`Tutanak Düzenle: ${report.incident_no}`} size="lg">
      <div className="space-y-3" data-testid="ir-edit-form">
        <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-900">
          <b>ℹ</b> Kaydettiğinizde eski sürüm otomatik versiyon arşivine alınır. Yalnız Sistem Yöneticisi düzenleyebilir.
        </div>
        <div>
          <label className="text-xs uppercase text-slate-500 flex items-center gap-1"><Users size={12}/> İlgili Personel *</label>
          <div className="max-h-32 overflow-y-auto border rounded p-2 space-y-1">
            {employees.map(e => (
              <label key={e.id} className="flex items-center gap-2 text-xs cursor-pointer">
                <input type="checkbox" checked={form.employee_ids.includes(e.id)}
                       onChange={() => toggleEmp(e.id)} data-testid={`ir-edit-emp-${e.id}`}/>
                <span>{e.first_name} {e.last_name} <span className="text-slate-400">· {e.role || "—"}</span></span>
              </label>
            ))}
          </div>
          <div className="text-[10px] text-slate-500 mt-1">{form.employee_ids.length} seçili</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <Input label="Olay Tarihi *" type="date" value={form.incident_date}
                 onChange={e => setForm({ ...form, incident_date: e.target.value })} data-testid="ir-edit-date"/>
          <Input label="Olay Saati" type="time" value={form.incident_time}
                 onChange={e => setForm({ ...form, incident_time: e.target.value })} data-testid="ir-edit-time"/>
          <Input label="Olay Yeri" value={form.incident_place}
                 onChange={e => setForm({ ...form, incident_place: e.target.value })} data-testid="ir-edit-place"/>
        </div>
        <Textarea label="Olay Açıklaması *" rows={5} value={form.incident_description}
                  onChange={e => setForm({ ...form, incident_description: e.target.value })} data-testid="ir-edit-desc"/>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <WitnessPicker label="Şahit 1" empKey="witness_1_employee_id" nameKey="witness_1_name"
                         form={form} setForm={setForm} employees={employees} tid="ir-edit-w1"/>
          <WitnessPicker label="Şahit 2" empKey="witness_2_employee_id" nameKey="witness_2_name"
                         form={form} setForm={setForm} employees={employees} tid="ir-edit-w2"/>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Btn variant="secondary" onClick={onClose} data-testid="ir-edit-cancel">Vazgeç</Btn>
          <Btn onClick={save} disabled={busy} data-testid="ir-edit-save">
            <FileText size={14}/> Kaydet (Yeni Versiyon)
          </Btn>
        </div>
      </div>
    </Modal>
  );
}

// ===================================================================
// Detay / İş Akışı Paneli — FAZ 3 (AI Öneri) + FAZ 4 (Savunma Akışı)
// ===================================================================
function DetailWorkflow({ doc, onBack, onPdf, onRefresh, isAdmin, onEdit }) {
  const [busy, setBusy] = useState(false);
  const [busyKey, setBusyKey] = useState("");
  const [error, setError] = useState("");

  const call = async (key, fn) => {
    setBusy(true); setBusyKey(key); setError("");
    try { await fn(); await onRefresh(); }
    catch (e) { setError(e.response?.data?.detail || e.message || "İşlem başarısız"); }
    setBusy(false); setBusyKey("");
  };

  return (
    <div className="max-h-[75vh] overflow-y-auto space-y-3 pr-1" data-testid="ir-workflow">
      <div className="flex justify-between items-center sticky top-0 bg-white z-10 pb-2 border-b">
        <div className="flex items-center gap-2">
          <Btn size="sm" variant="secondary" onClick={onBack} data-testid="ir-back"><ArrowLeft size={12}/> Geri</Btn>
          <span className="font-mono text-indigo-700 font-semibold">{doc.incident_no}</span>
          <StatusBadge s={doc.status}/>
          <span className="text-[10px] text-slate-500">v{doc.version || 1}</span>
        </div>
        <div className="flex gap-2">
          {isAdmin && onEdit && (
            <Btn size="sm" variant="secondary" onClick={onEdit} data-testid="ir-detail-edit"
                 className="!bg-amber-100 hover:!bg-amber-200 !text-amber-800">
              <Pencil size={12}/> Düzenle
            </Btn>
          )}
          <Btn size="sm" variant="secondary" onClick={onPdf} data-testid="ir-detail-pdf"><Eye size={12}/> PDF</Btn>
        </div>
      </div>

      {error && (
        <div className="bg-rose-50 border border-rose-200 text-rose-800 rounded p-2 text-xs flex items-start gap-2">
          <AlertTriangle size={14} className="mt-0.5 shrink-0"/>
          <span>{error}</span>
        </div>
      )}

      {/* Özet — iter293: Belirgin Düzenle butonu doğrudan bu kartın başına eklendi.
          Kullanıcı geri bildirimi: üst header'daki buton dikkat çekmiyor, bu kartta olsun. */}
      <div className="bg-slate-50 border rounded p-3 text-xs space-y-1" data-testid="ir-workflow-summary">
        <div className="flex items-center justify-between mb-1">
          <div className="text-[10px] uppercase text-slate-500 font-semibold">Oluşturulan Tutanak</div>
          {onEdit && (
            <Btn size="sm" variant="secondary" onClick={onEdit} data-testid="ir-summary-edit"
                 className="!bg-amber-100 hover:!bg-amber-200 !text-amber-800 !py-0.5">
              <Pencil size={11}/> Düzenle
            </Btn>
          )}
        </div>
        <div><b>Olay:</b> {doc.incident_date} {doc.incident_time || ""} · <b>Yer:</b> {doc.incident_place || "—"}</div>
        <div><b>Personel:</b> {(doc.employees || []).map(e => e.name).join(", ")}</div>
        {(doc.witness_1_name || doc.witness_2_name) && (
          <div><b>Şahitler:</b> {[doc.witness_1_name, doc.witness_2_name].filter(Boolean).join(" · ")}</div>
        )}
        <div className="text-slate-700 whitespace-pre-wrap"><b>Açıklama:</b> {doc.incident_description}</div>
      </div>

      {/* FAZ 3 — AI ÖNERİ */}
      <AiSuggestPanel doc={doc} call={call} busy={busy} busyKey={busyKey}/>

      {/* iter289 — Genel Ek Belge Yükleme (savunma dışı) */}
      <AttachmentsPanel doc={doc} onRefresh={onRefresh}/>

      {/* FAZ 4 — SAVUNMA AKIŞI */}
      <DefenseFlowPanel doc={doc} call={call} busy={busy} busyKey={busyKey}/>

      {/* FAZ 4 — İŞVEREN DEĞERLENDİRME + KAPATMA */}
      <EmployerDecisionPanel doc={doc} call={call} busy={busy} busyKey={busyKey}/>
    </div>
  );
}

// -------------------- FAZ 3: AI Öneri + Yasal Kontrol ----------------
function AiSuggestPanel({ doc, call, busy, busyKey }) {
  const sug = doc.ai_suggestion || null;
  const locked = ["signed_uploaded", "defense_requested", "defense_received",
                  "evaluation_completed", "closed"].includes(doc.status);

  const generate = () =>
    call("ai-suggest", async () => {
      await api.post(`/personnel/incident-reports/${doc.id}/ai-suggest`);
    });
  const apply = () =>
    call("ai-apply", async () => {
      if (!window.confirm("AI önerisi olay açıklaması olarak uygulanacak. Eski metin versiyon arşivine gönderilecek. Devam?")) return;
      await api.post(`/personnel/incident-reports/${doc.id}/apply-ai`);
    });

  return (
    <div className="border rounded p-3 bg-violet-50/40 border-violet-200" data-testid="ir-ai-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="font-semibold text-sm text-violet-900 flex items-center gap-1.5">
          <Sparkles size={14}/> Faz 3 — AI Öneri + Yasal Kontrol
        </div>
        <div className="flex gap-1.5">
          <Btn size="sm" variant="secondary" onClick={generate}
               disabled={busy || locked} data-testid="ir-ai-generate">
            {busyKey === "ai-suggest" ? <Loader2 size={12} className="animate-spin"/> : <Sparkles size={12}/>}
            {sug ? "Yeniden Üret" : "AI ile Hazırla"}
          </Btn>
          {sug && !locked && (
            <Btn size="sm" onClick={apply} disabled={busy} data-testid="ir-ai-apply">
              <CheckCircle2 size={12}/> Metne Uygula
            </Btn>
          )}
        </div>
      </div>
      <div className="text-[10px] text-violet-800 mb-2 flex items-start gap-1">
        <Scale size={11} className="mt-0.5"/>
        <span>Sonuçlar <b>öneri</b> niteliğindedir. Otomatik yazma <b>yok</b>. KVKK maskeli (TC/telefon/e-posta).</span>
      </div>
      {!sug && (
        <div className="text-xs text-slate-500 italic py-2">
          Henüz AI önerisi üretilmedi. "AI ile Hazırla" butonuna basın; sistem olay metnini yeniden ifade eder ve yasal risk kontrolü yapar.
        </div>
      )}
      {sug && (
        <div className="space-y-2 text-xs">
          <div className="bg-white border rounded p-2">
            <div className="text-[10px] uppercase text-slate-500 font-semibold mb-1">Önerilen Yeniden İfade</div>
            <div className="whitespace-pre-wrap text-slate-800" data-testid="ir-ai-text">{sug.suggested_description}</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div className="bg-white border rounded p-2">
              <div className="text-[10px] uppercase text-slate-500 font-semibold mb-1">Yasal Değerlendirme</div>
              <div className="text-slate-700">{sug.legal_check?.compliance_notes || "—"}</div>
              <div className="mt-1"><b>Ağırlık:</b> <span className="uppercase">{sug.legal_check?.severity_estimate || "—"}</span></div>
              {(sug.legal_check?.["iş_kanunu_maddeleri"] || sug.legal_check?.is_kanunu_maddeleri || []).length > 0 && (
                <div className="mt-1"><b>İş Kanunu:</b> {(sug.legal_check?.["iş_kanunu_maddeleri"] || sug.legal_check?.is_kanunu_maddeleri || []).join(", ")}</div>
              )}
              {(sug.legal_check?.potential_issues || []).length > 0 && (
                <ul className="list-disc ml-4 mt-1">
                  {(sug.legal_check?.potential_issues || []).map((p, i) => <li key={i}>{p}</li>)}
                </ul>
              )}
            </div>
            <div className="bg-white border rounded p-2">
              <div className="text-[10px] uppercase text-slate-500 font-semibold mb-1">Önerilen Eylemler</div>
              {(sug.advisory_actions || []).length === 0 && <div className="text-slate-400 italic">—</div>}
              <ul className="list-disc ml-4 space-y-1">
                {(sug.advisory_actions || []).map((a, i) => <li key={i}>{a}</li>)}
              </ul>
            </div>
          </div>
          <div className="text-[10px] text-slate-400 italic">
            Model: {sug.provider_used}/{sug.model_used} · Üretim: {(sug.generated_at || "").slice(0, 16).replace("T", " ")}
          </div>
        </div>
      )}
    </div>
  );
}

// -------------------- FAZ 4: Savunma Akışı ---------------------------
function DefenseFlowPanel({ doc, call, busy, busyKey }) {
  const [deadline, setDeadline] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 3);
    return d.toISOString().slice(0, 10);
  });
  const [reqNote, setReqNote] = useState("");
  const [defText, setDefText] = useState("");
  const [defFile, setDefFile] = useState(null);
  // iter292 — Ek Belgeler'e yüklenmiş "Personel Savunması" attachment seçimi
  const defenseAtts = (doc.attachments || []).filter(a => a.category === "employee_defense");
  const [selAttId, setSelAttId] = useState("");

  const canRequest = ["draft", "prepared", "signed_uploaded"].includes(doc.status);
  // iter291 — Savunma yükleme kısıtı gevşetildi: personel savunma vermezse
  // veya dosyayla verirse tekrar yükleme yapılabilir. "defense_received"
  // olsa bile yeni dosya yüklemek isteyene açık olsun ki hatalı dosya
  // düzeltilebilsin.
  const canUpload = ["defense_requested", "defense_received"].includes(doc.status);
  const request = () =>
    call("req-def", async () => {
      if (!deadline) return alert("Son tarih zorunlu");
      await api.post(`/personnel/incident-reports/${doc.id}/request-defense`,
        { deadline_date: deadline, note: reqNote || null });
      setReqNote("");
    });
  const upload = () =>
    call("upl-def", async () => {
      if (!defText.trim() && !defFile) return alert("Metin veya dosya girişi zorunlu");
      const fd = new FormData();
      if (defText.trim()) fd.append("defense_text", defText.trim());
      if (defFile) fd.append("file", defFile);
      await api.postForm(`/personnel/incident-reports/${doc.id}/upload-defense`, fd);
      setDefText(""); setDefFile(null);
    });
  const evalAi = () =>
    call("ai-def", async () => {
      await api.post(`/personnel/incident-reports/${doc.id}/ai-evaluate-defense`);
    });
  // iter292 — Ek Belgeler'den seçili "Personel Savunması" ile AI değerlendir
  const evalAiFromAttachment = () =>
    call("ai-def-att", async () => {
      if (!selAttId) return alert("Önce yüklü bir Personel Savunması belgesi seçin");
      await api.post(`/personnel/incident-reports/${doc.id}/ai-evaluate-defense-attachment`,
        { attachment_id: selAttId });
      setSelAttId("");
    });

  const dreq = doc.defense_request;
  const dres = doc.defense_response;
  const aidef = doc.ai_defense_evaluation;

  return (
    <div className="border rounded p-3 bg-amber-50/40 border-amber-200" data-testid="ir-defense-panel">
      <div className="font-semibold text-sm text-amber-900 mb-2 flex items-center gap-1.5">
        <ShieldQuestion size={14}/> Faz 4 — Savunma Akışı
      </div>

      {/* 1. Savunma Talep Et */}
      <div className="bg-white border rounded p-2 mb-2">
        <div className="text-xs font-semibold text-slate-700 mb-1">1) Savunma Talep Et</div>
        {dreq ? (
          <div className="text-xs text-slate-700">
            <div>Talep: {(dreq.requested_at || "").slice(0, 10)} · Son tarih: <b>{dreq.deadline_date}</b> · Talep eden: {dreq.requested_by}</div>
            {dreq.note && <div className="mt-1 whitespace-pre-wrap text-slate-600 italic">{dreq.note}</div>}
          </div>
        ) : (
          canRequest && (
            <div className="flex flex-col gap-2">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <Input label="Son Tarih" type="date" value={deadline}
                       onChange={e => setDeadline(e.target.value)} data-testid="ir-def-deadline"/>
                <div className="md:col-span-2">
                  <Textarea label="Not (Personele iletilecek)" rows={2} value={reqNote}
                            onChange={e => setReqNote(e.target.value)} data-testid="ir-def-note"/>
                </div>
              </div>
              <div className="flex justify-end">
                <Btn size="sm" onClick={request} disabled={busy} data-testid="ir-def-request">
                  {busyKey === "req-def" ? <Loader2 size={12} className="animate-spin"/> : <ShieldQuestion size={12}/>} Talep Gönder
                </Btn>
              </div>
            </div>
          )
        )}
        {!dreq && !canRequest && <div className="text-xs text-slate-400 italic">Bu aşamada savunma talep edilemez.</div>}
      </div>

      {/* 2. Savunma Yükle */}
      {(dreq || dres) && (
        <div className="bg-white border rounded p-2 mb-2">
          <div className="text-xs font-semibold text-slate-700 mb-1">2) Personelden Alınan Savunma</div>
          {dres && (
            <div className="text-xs text-slate-700 space-y-1 bg-slate-50 rounded p-2 mb-2">
              <div>Alınma: {(dres.received_at || "").slice(0, 10)} · Yöntem: <b>{dres.method}</b></div>
              {dres.text && <div className="mt-1 whitespace-pre-wrap bg-white rounded p-1.5 border">{dres.text}</div>}
              {dres.file && (
                <div className="mt-1">
                  <Btn size="sm" variant="secondary" onClick={async () => {
                    try {
                      const blob = await api.getBlob(`/personnel/incident-reports/${doc.id}/defense-file`);
                      const url = URL.createObjectURL(blob);
                      window.open(url, "_blank");
                      setTimeout(() => URL.revokeObjectURL(url), 60000);
                    } catch (e) { alert(e.response?.data?.detail || "İndirilemedi"); }
                  }} data-testid="ir-def-file-open">
                    <Paperclip size={11}/> {dres.file.original_name} ({(dres.file.size/1024).toFixed(1)} KB)
                  </Btn>
                </div>
              )}
            </div>
          )}
          {canUpload && (
            <div className="flex flex-col gap-2 border-t pt-2">
              <div className="text-[11px] text-indigo-700 italic bg-indigo-50 rounded p-1.5">
                <b>iter291:</b> Personel savunmasını iki şekilde yükleyebilirsiniz — <b>metin</b> alanına yazın
                veya <b>PDF/JPG/PNG</b> dosyası yükleyin. AI, dosya-only savunmaları da otomatik okuyup değerlendirir.
                {dres && " Yeni yükleme mevcut savunmanın üzerine yazar."}
              </div>
              <Textarea label="Savunma Metni (opsiyonel — personelin yazdığı savunma)" rows={4} value={defText}
                        onChange={e => setDefText(e.target.value)} data-testid="ir-def-text"/>
              <div>
                <label className="text-[10px] uppercase text-slate-500 font-semibold flex items-center gap-1">
                  <Upload size={11}/> Dosya (PDF / JPG / PNG — el yazısı savunma da olabilir, AI okur)
                </label>
                <input type="file" onChange={e => setDefFile(e.target.files?.[0] || null)}
                       accept=".pdf,image/png,image/jpeg,image/jpg"
                       data-testid="ir-def-file"
                       className="w-full text-xs file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"/>
              </div>
              <div className="flex justify-end">
                <Btn size="sm" onClick={upload} disabled={busy} data-testid="ir-def-upload">
                  {busyKey === "upl-def" ? <Loader2 size={12} className="animate-spin"/> : <Upload size={12}/>}
                  {dres ? "Yeniden Yükle" : "Yükle"}
                </Btn>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 3. AI Değerlendirme */}
      {(dres || defenseAtts.length > 0) && (
        <div className="bg-white border rounded p-2">
          <div className="flex items-center justify-between mb-1">
            <div className="text-xs font-semibold text-slate-700">3) AI Savunma Değerlendirmesi <span className="text-slate-400 font-normal">(öneri)</span></div>
            {dres && ["defense_received", "evaluation_completed"].includes(doc.status) && (
              <Btn size="sm" variant="secondary" onClick={evalAi} disabled={busy} data-testid="ir-ai-def-run">
                {busyKey === "ai-def" ? <Loader2 size={12} className="animate-spin"/> : <Sparkles size={12}/>}
                {aidef ? "Yeniden Üret" : "AI ile Değerlendir"}
              </Btn>
            )}
          </div>

          {/* iter292 — Ek Belgeler'e yüklenen Personel Savunması belgelerinden seç */}
          {defenseAtts.length > 0 && (
            <div className="bg-indigo-50 border border-indigo-200 rounded p-2 mb-2" data-testid="ir-def-att-picker">
              <div className="text-[11px] text-indigo-900 mb-1.5">
                <b>Ek Belgeler'den seç:</b> "Personel Savunması" kategorisinde yüklü belgeler bulundu. AI, seçilen belgeyi doğrudan okuyup değerlendirir (PDF → metin, görsel → Vision OCR).
              </div>
              <div className="flex gap-2 items-end flex-wrap">
                <div className="flex-1 min-w-[220px]">
                  <label className="text-[10px] uppercase text-slate-500 font-semibold">Personel Savunması Belgesi</label>
                  <Select value={selAttId} onChange={e => setSelAttId(e.target.value)} data-testid="ir-def-att-sel">
                    <option value="">— Belge seç —</option>
                    {defenseAtts.map(a => (
                      <option key={a.id} value={a.id}>
                        {a.original_name} ({(a.size/1024).toFixed(1)} KB · {(a.uploaded_at || "").slice(0,10)})
                      </option>
                    ))}
                  </Select>
                </div>
                <Btn size="sm" onClick={evalAiFromAttachment} disabled={busy || !selAttId}
                     data-testid="ir-ai-def-from-att">
                  {busyKey === "ai-def-att" ? <Loader2 size={12} className="animate-spin"/> : <Sparkles size={12}/>}
                  Seçilen Belgeyle AI Değerlendir
                </Btn>
              </div>
            </div>
          )}

          {dres && !dres.text && dres.file && (
            <div className="text-[11px] text-indigo-700 italic bg-indigo-50 rounded p-1.5 mb-1">
              <b>iter287:</b> Savunma dosya olarak yüklenmiş. AI dosyayı doğrudan okuyacak
              (PDF → metin çıkarımı, görsel → Gemini Vision transkripsiyon).
            </div>
          )}
          {aidef ? (
            <div className="text-xs text-slate-700 space-y-1.5 mt-1">
              {aidef.source_attachment_name && (
                <div className="text-[10px] text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-1.5 py-0.5 inline-block">
                  Kaynak: {aidef.source_attachment_name}
                </div>
              )}
              <div><b>Özet:</b> {aidef.defense_summary}</div>
              <div><b>Değerlendirme:</b> {aidef.credibility_assessment}</div>
              {(aidef.mitigating_factors || []).length > 0 && (
                <div>
                  <b className="text-emerald-700">Hafifletici Faktörler:</b>
                  <ul className="list-disc ml-5">{aidef.mitigating_factors.map((f,i) => <li key={i}>{f}</li>)}</ul>
                </div>
              )}
              {(aidef.aggravating_factors || []).length > 0 && (
                <div>
                  <b className="text-rose-700">Ağırlaştırıcı Faktörler:</b>
                  <ul className="list-disc ml-5">{aidef.aggravating_factors.map((f,i) => <li key={i}>{f}</li>)}</ul>
                </div>
              )}
              <div className="bg-indigo-50 border border-indigo-200 rounded p-1.5">
                <b>Öneri:</b> {aidef.recommendation}
              </div>
              <div className="text-[10px] text-slate-400 italic">
                Model: {aidef.provider_used}/{aidef.model_used} · {(aidef.generated_at || "").slice(0, 16).replace("T", " ")}
              </div>
            </div>
          ) : (
            <div className="text-xs text-slate-400 italic mt-1">Henüz AI değerlendirmesi yok.</div>
          )}
        </div>
      )}
    </div>
  );
}

// -------------------- FAZ 4: İşveren Kararı + Kapatma ----------------
function EmployerDecisionPanel({ doc, call, busy, busyKey }) {
  const [decision, setDecision] = useState(doc.evaluation?.decision || "verbal_warning");
  const [note, setNote] = useState("");
  // iter292 — Admin doğrudan mevcut değerlendirmeyi düzenleyebilsin
  const user = (() => { try { return JSON.parse(localStorage.getItem("user") || "{}"); } catch { return {}; } })();
  const isAdmin = user?.role === "admin";
  const [editing, setEditing] = useState(false);

  const canEvaluate = ["defense_received", "defense_requested", "evaluation_completed"].includes(doc.status);
  const canClose = doc.status === "evaluation_completed";
  const ev = doc.evaluation;
  const hasAiDef = !!doc.ai_defense_evaluation;

  const submit = () =>
    call("employer", async () => {
      if (!note.trim()) return alert("Karar gerekçesi zorunlu");
      await api.post(`/personnel/incident-reports/${doc.id}/employer-evaluation`,
        { decision, note });
      setNote(""); setEditing(false);
    });
  const close = () =>
    call("close", async () => {
      if (!window.confirm("Tutanağı kapatmak istediğinizden emin misiniz?")) return;
      await api.post(`/personnel/incident-reports/${doc.id}/close`);
    });
  // iter292 — AI ile otomatik nihai değerlendirme taslağı üret
  const autoFill = () =>
    call("auto-emp", async () => {
      if (!hasAiDef) return alert("Önce Faz 4 AI Savunma Değerlendirmesi'ni üretin");
      if (ev && !window.confirm("Mevcut nihai değerlendirmenin üzerine AI tarafından yeni bir taslak yazılacak. Devam?")) return;
      await api.post(`/personnel/incident-reports/${doc.id}/auto-employer-evaluation`);
    });

  const startEdit = () => {
    setDecision(ev?.decision || "verbal_warning");
    setNote(ev?.note || "");
    setEditing(true);
  };

  return (
    <div className="border rounded p-3 bg-indigo-50/40 border-indigo-200" data-testid="ir-decision-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="font-semibold text-sm text-indigo-900 flex items-center gap-1.5">
          <Gavel size={14}/> Faz 4 — İşveren Nihai Değerlendirmesi
        </div>
        {hasAiDef && (
          <Btn size="sm" variant="secondary" onClick={autoFill} disabled={busy}
               data-testid="ir-auto-emp-btn"
               className="!bg-violet-100 hover:!bg-violet-200 !text-violet-800">
            {busyKey === "auto-emp" ? <Loader2 size={12} className="animate-spin"/> : <Sparkles size={12}/>}
            {ev ? "AI ile Yeniden Doldur" : "AI ile Otomatik Doldur"}
          </Btn>
        )}
      </div>
      {ev && !editing && (
        <div className="bg-white border rounded p-2 text-xs text-slate-700 mb-2">
          <div className="flex items-center justify-between mb-1 flex-wrap gap-1">
            <div className="flex items-center gap-2">
              <b>Karar:</b> <span className="text-indigo-800 font-semibold">{DECISION_MAP[ev.decision] || ev.decision}</span>
              {ev.auto_generated && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-100 text-violet-800 border border-violet-200 font-semibold" data-testid="ir-eval-auto-badge">
                  AI Taslak
                </span>
              )}
            </div>
            <div className="flex gap-1">
              {isAdmin && (
                <Btn size="sm" variant="secondary" onClick={startEdit} data-testid="ir-eval-edit"
                     className="!bg-amber-100 hover:!bg-amber-200 !text-amber-800">
                  <Pencil size={11}/> Düzenle
                </Btn>
              )}
              {/* iter287 — GMP Nihai Değerlendirme PDF */}
              <Btn size="sm" variant="secondary" onClick={async () => {
                try {
                  const blob = await api.getBlob(`/personnel/incident-reports/${doc.id}/evaluation-pdf`);
                  const url = URL.createObjectURL(blob);
                  window.open(url, "_blank");
                  setTimeout(() => URL.revokeObjectURL(url), 60000);
                } catch (e) { alert(e?.response?.data?.detail || "PDF alınamadı"); }
              }} data-testid="ir-eval-pdf-btn">
                <Eye size={11}/> PDF
              </Btn>
            </div>
          </div>
          <div>Karar Tarihi: {(ev.decided_at || "").slice(0, 10)} · Veren: {ev.decided_by}</div>
          <div className="mt-1 whitespace-pre-wrap">{ev.note}</div>
          {ev.auto_generated && !ev.ai_source?.classifier_ok && (
            <div className="mt-1 text-[10px] text-amber-800 italic">
              ⚠ AI sınıflandırıcı yanıt vermediği için karar `written_warning` fallback'ine alındı. Admin gerekirse düzenlesin.
            </div>
          )}
        </div>
      )}
      {(canEvaluate && !ev) || editing ? (
        <div className="space-y-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div>
              <label className="text-xs uppercase text-slate-500">Karar</label>
              <Select value={decision} onChange={e => setDecision(e.target.value)} data-testid="ir-decision-sel">
                {Object.entries(DECISION_MAP).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </Select>
            </div>
          </div>
          <Textarea label="Karar Gerekçesi *" rows={3} value={note}
                    onChange={e => setNote(e.target.value)} data-testid="ir-decision-note"/>
          <div className="flex justify-end gap-2">
            {editing && (
              <Btn size="sm" variant="secondary" onClick={() => setEditing(false)} data-testid="ir-decision-cancel">
                Vazgeç
              </Btn>
            )}
            <Btn size="sm" onClick={submit} disabled={busy} data-testid="ir-decision-save">
              {busyKey === "employer" ? <Loader2 size={12} className="animate-spin"/> : <Gavel size={12}/>}
              {ev ? "Kararı Güncelle" : "Kararı Kaydet"}
            </Btn>
            {canClose && !editing && (
              <Btn size="sm" variant="secondary" onClick={close} disabled={busy} data-testid="ir-close">
                {busyKey === "close" ? <Loader2 size={12} className="animate-spin"/> : <CheckCircle2 size={12}/>} Tutanağı Kapat
              </Btn>
            )}
          </div>
        </div>
      ) : null}
      {ev && !editing && canClose && (
        <div className="flex justify-end">
          <Btn size="sm" variant="secondary" onClick={close} disabled={busy} data-testid="ir-close">
            {busyKey === "close" ? <Loader2 size={12} className="animate-spin"/> : <CheckCircle2 size={12}/>} Tutanağı Kapat
          </Btn>
        </div>
      )}
      {!canEvaluate && !ev && (
        <div className="text-xs text-slate-400 italic">Nihai değerlendirme savunma alındıktan sonra yapılabilir.</div>
      )}
    </div>
  );
}
